import { useMemo } from 'react';
import { FacetInfo } from '../../../borrowed/catalog-app/typescript/src/app/model/types';

import {
  FacetModifier,
  FacetParseInfo,
  GroupingTokenType,
  MUST_NOT_TOKEN,
  MUST_TOKEN,
  SEARCH_DELIMITER,
  SEARCH_FACET_END_TOKEN,
  SuggestionParseInfo,
  SuggestionsProps,
} from '../shared';

const OpeningCharacterR = new RegExp('[\\[\\{\\("]');

export const useCatalogSearchParser = ({
  currentText,
  selectionStartPosition = -1,
  selectionEndPosition = -1,
  facetsForCurrentIndices,
}: Pick<SuggestionsProps, 'currentText' | 'selectionStartPosition' | 'selectionEndPosition'> & {
  facetsForCurrentIndices?: Record<string, FacetInfo>;
}): SuggestionParseInfo | null => {
  const parseInfo: SuggestionParseInfo | null = useMemo(() => {
    if (facetsForCurrentIndices) {
      return parseFacetInfo({
        currentText,
        allFacets: facetsForCurrentIndices,
        selectionStartPosition,
        selectionEndPosition,
      });
    }
    return null;
  }, [currentText, facetsForCurrentIndices, selectionStartPosition, selectionEndPosition]);
  return parseInfo;
};

const parseFacetInfo = (opts: {
  currentText: string | undefined;
  selectionStartPosition: number;
  selectionEndPosition: number;
  allFacets: Record<string, FacetInfo>;
}): SuggestionParseInfo | null => {
  if (opts.currentText === undefined) return null;
  const sectionInfo: SuggestionParseInfo = {
    startIndex: 0,
    endIndex: 0,
    value: '',
    // facet: '',
    parsedFacet: '',
    facetInfo: null,
    suggestionType: 'facet',
  };
  const { currentText: inputText, allFacets, selectionStartPosition } = opts;
  const currentText =
    selectionStartPosition < 0 || selectionStartPosition >= inputText.length
      ? inputText
      : inputText.substring(0, selectionStartPosition);
  let groupingTokenType: GroupingTokenType = 'none';
  const parentFacetInfo: FacetParseInfo | null = null; // SOPHIA TODO

  const [firstUnclosedGroupingCharIndex, unclosedTokenType] = findUnclosedGroupingCharacter(
    currentText
  );
  if (unclosedTokenType !== undefined) {
    const startToUnclosed = currentText.substring(
      0,
      firstUnclosedGroupingCharIndex > -1 ? firstUnclosedGroupingCharIndex : undefined
    );
    const unclosedToEnd = currentText.substring(
      firstUnclosedGroupingCharIndex > -1 ? firstUnclosedGroupingCharIndex + 1 : 0
    );
    // Use the original input text when determining the end index
    const facetEndTokenIndex: number = startToUnclosed.lastIndexOf(SEARCH_FACET_END_TOKEN);
    const partsBeforeUnclosed: string[] = startToUnclosed.split(SEARCH_FACET_END_TOKEN);
    // Does at least one full facet exist in the input?
    if (facetEndTokenIndex > -1) {
      groupingTokenType = getGroupingTokenType(
        firstUnclosedGroupingCharIndex > -1
          ? currentText.charAt(firstUnclosedGroupingCharIndex)
          : ''
      );

      const partsBeforeLastFacetEndToken = partsBeforeUnclosed[partsBeforeUnclosed.length - 2]
        .trim()
        .split(SEARCH_DELIMITER);

      // Remove leading whitespace, but trailing whitespace is important when determining if we are in a value or starting a new facet
      const tokensAfterFacet =
        groupingTokenType === 'none' ? unclosedToEnd.trimLeft().split(SEARCH_DELIMITER) : [];

      if (tokensAfterFacet.length <= 1) {
        if (
          collectValueSectionInfo({
            sectionInfo,
            allFacets,
            currentText,
            facetEndTokenIndex,
            parentFacetInfo,
            groupingTokenType,
            partsBeforeLastFacetEndToken,
            untrimmedValue: currentText.substring(
              firstUnclosedGroupingCharIndex > -1 ? firstUnclosedGroupingCharIndex : 0
            ),
          })
        ) {
          return sectionInfo;
        }
      }
      collectFacetSectionInfo({
        sectionInfo,
        allFacets,
        currentText,
        groupingTokenType,
        parentFacetInfo,
        partsBeforeLastFacetEndToken,
        tokensAfterFacet,
        untrimmedValue: currentText.substring(
          firstUnclosedGroupingCharIndex > -1 ? firstUnclosedGroupingCharIndex : 0
        ),
        facetEndTokenIndex,
      });
      return sectionInfo;
    }
  }

  // Use the original input text when determining the end index
  const facetEndTokenIndex = currentText.lastIndexOf(SEARCH_FACET_END_TOKEN);
  groupingTokenType = 'none';
  const parts: string[] = currentText.split(SEARCH_FACET_END_TOKEN);

  // Does at least one full facet exist in the input?
  if (facetEndTokenIndex > -1) {
    const section = parts[parts.length - 1];
    groupingTokenType = getGroupingTokenType(section);

    const partsBeforeLastFacetEndToken = parts[parts.length - 2].trim().split(SEARCH_DELIMITER);
    const untrimmedValue = parts[parts.length - 1];
    // Remove leading whitespace, but trailing whitespace is important when determining if we are in a value or starting a new facet
    const tokensAfterFacet =
      groupingTokenType === 'none' ? untrimmedValue.trimLeft().split(SEARCH_DELIMITER) : [];

    if (tokensAfterFacet.length <= 1) {
      if (
        collectValueSectionInfo({
          sectionInfo,
          allFacets,
          currentText,
          facetEndTokenIndex,
          parentFacetInfo,
          groupingTokenType,
          partsBeforeLastFacetEndToken,
          untrimmedValue,
        })
      ) {
        return sectionInfo;
      }
    }
    collectFacetSectionInfo({
      sectionInfo,
      allFacets,
      currentText,
      groupingTokenType,
      parentFacetInfo,
      partsBeforeLastFacetEndToken,
      tokensAfterFacet,
      untrimmedValue,
      facetEndTokenIndex,
    });
    return sectionInfo;
  }
  // We don't do free text suggestions at this time and no facets were found. We must be suggesting a facet.
  collectFacetSectionInfo({
    sectionInfo,
    allFacets,
    currentText,
    facetEndTokenIndex,
    groupingTokenType,
    parentFacetInfo,
    partsBeforeLastFacetEndToken: [],
    tokensAfterFacet: [],
    untrimmedValue: '',
  });

  return sectionInfo;
};

export const findUnclosedGroupingCharacter = (input: string): [number, GroupingTokenType?] => {
  // walk the string, from the start. finding any opening characters, find their matching closing character.
  // if no matching closing character exists, then we are still in that facet/level (will mean special handling for nested, eventually)
  let offset = 0;
  while (offset < input.length) {
    const openingCharacterInfo = findOpeningCharacter(input.substring(offset));
    if (openingCharacterInfo.index === -1) {
      // get outta here, no opening characters are unclosed.
      return [offset, undefined];
    }
    const groupingCharInfo = getCatalogSearchGroupingCharacters(
      openingCharacterInfo.groupingTokenType
    );
    const closingCharacterPosition = input.indexOf(
      groupingCharInfo.closing,
      offset + openingCharacterInfo.index + 1
    );
    if (closingCharacterPosition === -1) {
      // aha! we've got one.
      return [openingCharacterInfo.index + offset, openingCharacterInfo.groupingTokenType];
    } else {
      offset = closingCharacterPosition + 1;
    }
  }
  return [input.length, undefined];
};

const findOpeningCharacter = (
  input: string
): { index: number; groupingTokenType: GroupingTokenType } => {
  const openingCharacterInfo: { index: number; groupingTokenType: GroupingTokenType } = {
    index: -1,
    groupingTokenType: 'none',
  };
  const openingCharacterPosition: number = input.search(OpeningCharacterR);

  if (openingCharacterPosition > -1) {
    const openingChar = input.charAt(openingCharacterPosition);
    openingCharacterInfo.groupingTokenType = getGroupingTokenType(openingChar);
    if (openingCharacterInfo.groupingTokenType !== 'none') {
      openingCharacterInfo.index = openingCharacterPosition;
    }
  }
  return openingCharacterInfo;
};

const getGroupingTokenType = (openingChar: string): GroupingTokenType => {
  let groupingTokenType: GroupingTokenType = 'none';
  switch (openingChar) {
    case '(':
      groupingTokenType = 'paren';
      break;
    case '[':
      groupingTokenType = 'bracket';
      break;
    case '{':
      groupingTokenType = 'curly';
      break;
    case '"':
      groupingTokenType = 'quote';
      break;
  }
  return groupingTokenType;
};

const collectValueSectionInfo = (props: {
  sectionInfo: SuggestionParseInfo;
  allFacets: Record<string, FacetInfo>;
  currentText: string;
  facetEndTokenIndex: number;
  partsBeforeLastFacetEndToken: Array<string>;
  groupingTokenType: GroupingTokenType;
  untrimmedValue: string;
  parentFacetInfo: FacetParseInfo | null;
}): boolean => {
  const {
    sectionInfo,
    currentText,
    facetEndTokenIndex,
    partsBeforeLastFacetEndToken,
    allFacets,
    groupingTokenType,
    untrimmedValue,
    parentFacetInfo,
  } = { ...props };
  const updatedSectionInfo = { ...sectionInfo };
  updatedSectionInfo.suggestionType = 'value';
  updatedSectionInfo.startIndex = facetEndTokenIndex + 1; // if there are spaces following the facet end token they will be included in ..
  updatedSectionInfo.endIndex = currentText.length;
  const [parsedFacet, facetModifier] = digestFacet(
    partsBeforeLastFacetEndToken[partsBeforeLastFacetEndToken.length - 1].trim()
  );
  const parsedFacetLower: string = parsedFacet.toLocaleLowerCase();
  const facetInfo: FacetInfo | undefined = allFacets[parsedFacetLower];

  // Do we have enough information available to suggest values for a facet?
  if (facetInfo) {
    updatedSectionInfo.parsedFacet = parsedFacet;
    updatedSectionInfo.facetInfo = {
      ...facetInfo,
      groupingTokenType,
      parentFacetInfo,
      facetModifier,
      acceptedValue: '',
    };
    if (
      isGroupingTokenValidForFacet({
        facetInfo,
        groupingTokenType,
        currentValue: untrimmedValue,
      }) &&
      groupingTokenType !== 'none'
    ) {
      const enteredValue = untrimmedValue
        .substring(
          untrimmedValue.indexOf(getCatalogSearchGroupingCharacters(groupingTokenType).opening) + 1
        )
        .trim();
      if (groupingTokenType === 'paren') {
        const [firstUnclosedGroupingCharIndex, unclosedTokenType] = findUnclosedGroupingCharacter(
          enteredValue
        );
        if (unclosedTokenType === 'quote') {
          // We will only suggest on the last part of the input, anything with closed quotes within the parens is to be considered accepted.
          updatedSectionInfo.facetInfo.acceptedValue = enteredValue
            .substring(0, firstUnclosedGroupingCharIndex)
            .trim();
          updatedSectionInfo.value = enteredValue
            .substring(firstUnclosedGroupingCharIndex + 1)
            .trim();
        } else if (firstUnclosedGroupingCharIndex > -1 && unclosedTokenType === undefined) {
          updatedSectionInfo.facetInfo.acceptedValue = enteredValue
            .substring(0, firstUnclosedGroupingCharIndex)
            .trim();
          updatedSectionInfo.value = enteredValue.substring(firstUnclosedGroupingCharIndex).trim();
        } else {
          updatedSectionInfo.facetInfo.acceptedValue = '';
          updatedSectionInfo.value = enteredValue;
        }
      } else if (groupingTokenType === 'curly') {
        // TODO: For handling nested facets, we will will to distinguish between accepted and current for the sake of suggestions.
        updatedSectionInfo.facetInfo.acceptedValue = '';
        updatedSectionInfo.value = enteredValue;
      } else {
        // If there's only one possible value, then there is no accepted value for the section.
        updatedSectionInfo.facetInfo.acceptedValue = '';
        updatedSectionInfo.value = enteredValue;
      }
    } else {
      updatedSectionInfo.value = untrimmedValue.trim();
    }
    sectionInfo.endIndex = updatedSectionInfo.endIndex;
    sectionInfo.facetInfo = updatedSectionInfo.facetInfo;
    sectionInfo.parsedFacet = updatedSectionInfo.parsedFacet;
    sectionInfo.startIndex = updatedSectionInfo.startIndex;
    sectionInfo.suggestionType = updatedSectionInfo.suggestionType;
    sectionInfo.value = updatedSectionInfo.value;
    return true;
  }
  return false;
};

export interface CatalogSearchFacetGroupingCharacters {
  opening: string;
  closing: string;
  autoClose: boolean;
}
export const getCatalogSearchGroupingCharacters = (
  groupingTokenType: GroupingTokenType
): CatalogSearchFacetGroupingCharacters => {
  switch (groupingTokenType) {
    case 'paren':
      return { opening: '(', closing: ')', autoClose: false };
    case 'bracket':
      return { opening: '[', closing: ']', autoClose: false };
    case 'curly':
      return { opening: '{', closing: '}', autoClose: false };
    case 'quote':
      return { opening: '"', closing: '"', autoClose: true };
  }
  return { opening: '', closing: '', autoClose: false };
};

const collectFacetSectionInfo = (props: {
  sectionInfo: SuggestionParseInfo;
  allFacets: Record<string, FacetInfo>;
  currentText: string;
  partsBeforeLastFacetEndToken: Array<string>;
  groupingTokenType: GroupingTokenType;
  untrimmedValue: string; // ???
  facetEndTokenIndex: number;
  parentFacetInfo: FacetParseInfo | null;
  tokensAfterFacet: Array<string>;
}): boolean => {
  const {
    sectionInfo,
    currentText,
    partsBeforeLastFacetEndToken,
    allFacets,
    groupingTokenType,
    parentFacetInfo,
    facetEndTokenIndex,
    tokensAfterFacet,
  } = { ...props };
  // trailing whitespace is important when determining if we should be starting a new facet
  if (currentText.endsWith(SEARCH_DELIMITER)) {
    // hold up! We should be providing suggestions for an all new facet, as this one ends in a space.
    sectionInfo.parsedFacet = '';
    sectionInfo.facetInfo = null;
    sectionInfo.value = '';
    sectionInfo.startIndex = currentText.length;
    sectionInfo.endIndex = currentText.length;
    return true;
  }
  if (facetEndTokenIndex === -1) {
    const partsOfInput = currentText.split(SEARCH_DELIMITER);
    const [parsedFacet, facetModifier] = digestFacet(partsOfInput[partsOfInput.length - 1]);
    const parsedFacetLower: string = parsedFacet.toLocaleLowerCase();
    const facetInfo: FacetInfo | undefined = allFacets[parsedFacetLower];
    sectionInfo.parsedFacet = parsedFacet;
    sectionInfo.facetInfo = facetInfo
      ? {
          ...facetInfo,
          groupingTokenType,
          parentFacetInfo,
          facetModifier,
          acceptedValue: '',
        }
      : null;
    sectionInfo.startIndex = currentText.lastIndexOf(sectionInfo.parsedFacet);
    sectionInfo.endIndex = currentText.length;
    return true;
  }

  const [parsedFacet, facetModifier] = digestFacet(
    tokensAfterFacet.length > 1
      ? tokensAfterFacet[tokensAfterFacet.length - 1]
      : partsBeforeLastFacetEndToken[partsBeforeLastFacetEndToken.length - 1].trim()
  );

  const parsedFacetLower: string = parsedFacet.toLocaleLowerCase();
  const facetInfo: FacetInfo | undefined = allFacets[parsedFacetLower];
  sectionInfo.parsedFacet = parsedFacet;
  sectionInfo.facetInfo = facetInfo
    ? {
        ...facetInfo,
        groupingTokenType,
        parentFacetInfo,
        facetModifier,
        acceptedValue: '',
      }
    : null;
  sectionInfo.value = '';
  sectionInfo.startIndex = currentText.lastIndexOf(sectionInfo.parsedFacet);
  sectionInfo.endIndex = sectionInfo.startIndex + sectionInfo.parsedFacet.length;
  return true;
};

const digestFacet = (parsedFacet: string): [string, FacetModifier?] => {
  // It is expected that the input has already been trimmed. The modifier must be the
  // first character in the facet (it cannot be after the facet name, or before/after the value).
  switch (parsedFacet[0]) {
    case MUST_TOKEN:
      return [parsedFacet.substring(MUST_TOKEN.length), MUST_TOKEN];
    case MUST_NOT_TOKEN:
      return [parsedFacet.substring(MUST_NOT_TOKEN.length), MUST_NOT_TOKEN];
  }
  return [parsedFacet];
};

const isGroupingTokenValidForFacet = (props: {
  facetInfo: FacetInfo;
  groupingTokenType: GroupingTokenType;
  currentValue: string;
}): boolean => {
  return (
    // date can be bracket or quote or paren
    (props.facetInfo.valueType === 'date' &&
      (props.groupingTokenType === 'bracket' ||
        props.groupingTokenType === 'quote' ||
        props.groupingTokenType === 'paren' ||
        (props.groupingTokenType === 'none' && props.currentValue.trim().length === 0))) ||
    // keyword can be quote or none or paren
    (props.facetInfo.valueType === 'keyword' &&
      (props.groupingTokenType === 'quote' ||
        props.groupingTokenType === 'paren' ||
        props.groupingTokenType === 'none')) ||
    // text can be quote or none or paren
    (props.facetInfo.valueType === 'text' &&
      (props.groupingTokenType === 'quote' ||
        props.groupingTokenType === 'paren' ||
        props.groupingTokenType === 'none')) ||
    // nested can be curly, not paren
    (props.facetInfo.valueType === 'nested' && props.groupingTokenType === 'curly') ||
    (props.groupingTokenType === 'none' && props.currentValue.trim().length === 0) ||
    // float, long, and integer can be bracket or none
    ((props.facetInfo.valueType === 'float' ||
      props.facetInfo.valueType === 'long' ||
      props.facetInfo.valueType === 'integer') &&
      (props.groupingTokenType === 'none' || props.groupingTokenType === 'bracket'))
  );
};
